import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  List,
  Transfer,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  TeamOutlined,
  BankOutlined,
  SwapOutlined,
  SearchOutlined,
  EyeOutlined,
  WarningOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import api from "../../services/api";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";

const { Title, Text } = Typography;

const GuardCompanyManagement = () => {
  const { user } = useAuth();
  const isMultiCompanyPatrol = user?.role === "multi_company_patrol";
  const isSuperAdmin = user?.role === "super_admin";

  const [guards, setGuards] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create Guard modal
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm] = Form.useForm();
  const [createPinConflicts, setCreatePinConflicts] = useState([]);
  const [checkingCreatePin, setCheckingCreatePin] = useState(false);

  // Assign modal
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignGuard, setAssignGuard] = useState(null);
  const [assignCompanyIds, setAssignCompanyIds] = useState([]);
  const [assignPinConflicts, setAssignPinConflicts] = useState([]);
  const [checkingAssignPin, setCheckingAssignPin] = useState(false);
  const [assigning, setAssigning] = useState(false);

  // Detail modal
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedGuard, setSelectedGuard] = useState(null);
  const [guardDetail, setGuardDetail] = useState(null);

  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [guardsRes, companiesRes] = await Promise.all([
        api.get("/guard-company/guards"),
        api.get("/guard-company/companies"),
      ]);
      if (guardsRes.data.success) setGuards(guardsRes.data.data);
      if (companiesRes.data.success) setCompanies(companiesRes.data.data);
    } catch (error) {
      console.error("Fetch data error:", error);
      message.error("ไม่สามารถโหลดข้อมูลได้");
    } finally {
      setLoading(false);
    }
  };

  // ============ CREATE GUARD ============

  const handleOpenCreateModal = () => {
    createForm.resetFields();
    setCreatePinConflicts([]);
    setCreateModalVisible(true);
  };

  const handleCreatePinCheck = async () => {
    const pin = createForm.getFieldValue("password");
    const companyIds = createForm.getFieldValue("company_ids");
    if (
      !pin ||
      !/^\d{5}$/.test(pin) ||
      !companyIds ||
      companyIds.length === 0
    ) {
      setCreatePinConflicts([]);
      return;
    }

    try {
      setCheckingCreatePin(true);
      const res = await api.post("/guard-company/check-pin-conflict", {
        pin,
        company_ids: companyIds,
      });
      if (res.data.success) {
        setCreatePinConflicts(res.data.data.conflicts || []);
      }
    } catch (error) {
      console.error("PIN check error:", error);
    } finally {
      setCheckingCreatePin(false);
    }
  };

  const handleCreateGuard = async (values) => {
    try {
      setCreating(true);
      const res = await api.post("/guard-company/guards", values);
      if (res.data.success) {
        message.success(res.data.message);
        setCreateModalVisible(false);
        fetchData();
      }
    } catch (error) {
      const msg = error.response?.data?.message || "เพิ่ม รปภ. ล้มเหลว";
      message.error(msg);
    } finally {
      setCreating(false);
    }
  };

  // ============ ASSIGN GUARD ============

  const handleOpenAssignModal = (guard) => {
    setAssignGuard(guard);
    const assignedIds = getAssignedCompanyIds(guard);
    setAssignCompanyIds(assignedIds);
    setAssignModalVisible(true);
  };

  const handleAssignCompanyChange = async (companyIds) => {
    setAssignCompanyIds(companyIds);
  };

  const handleAssign = async () => {
    if (!assignGuard) {
      return;
    }

    try {
      setAssigning(true);
      const res = await api.post("/guard-company/assign", {
        user_id: assignGuard.id,
        company_ids: assignCompanyIds,
      });
      if (res.data.success) {
        message.success(res.data.message);
        setAssignModalVisible(false);
        fetchData();
      }
    } catch (error) {
      const msg = error.response?.data?.message || "มอบหมาย รปภ. ล้มเหลว";
      message.error(msg);
    } finally {
      setAssigning(false);
    }
  };

  // ============ DETAIL / REMOVE ============

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      const res = await api.delete(`/guard-company/assign/${assignmentId}`);
      if (res.data.success) {
        message.success(res.data.message);
        fetchData();
        if (selectedGuard) fetchGuardDetail(selectedGuard.id);
      }
    } catch (error) {
      message.error("ลบการมอบหมายล้มเหลว");
    }
  };

  const fetchGuardDetail = async (userId) => {
    try {
      const res = await api.get(`/guard-company/guards/${userId}/assignments`);
      if (res.data.success) setGuardDetail(res.data.data);
    } catch (error) {
      message.error("ไม่สามารถโหลดรายละเอียด รปภ. ได้");
    }
  };

  const handleViewDetail = async (guard) => {
    setSelectedGuard(guard);
    await fetchGuardDetail(guard.id);
    setDetailModalVisible(true);
  };

  const handleExport = () => {
    // 1. เตรียม Header ของตาราง
    const exportData = [
      ["ชื่อ รปภ.", "PIN (รหัสพนักงาน)", "สถานะ", "บริษัทที่รับผิดชอบ"],
    ];

    // 2. ลูปใส่ข้อมูลทีละแถว
    filteredGuards.forEach((g) => {
      const name = g.name || "-";
      const pin = g.employee_id || "-";
      const status = g.is_active ? "ใช้งานอยู่" : "ไม่ใช้งาน";

      if (!g.companies || g.companies.length === 0) {
        // กรณีไม่มีบริษัท
        exportData.push([name, pin, status, "-"]);
      } else {
        // กรณีมีบริษัท (อาจมีหลายบริษัท)
        g.companies.forEach((c, index) => {
          if (index === 0) {
            // บรรทัดแรกของ รปภ. คนนี้ ให้ใส่ชื่อและข้อมูลอื่นๆ ให้ครบ
            exportData.push([name, pin, status, c.company_name]);
          } else {
            // บรรทัดถัดๆ ไป (บริษัทที่ 2 เป็นต้นไป) ให้เว้นข้อมูล Guard ไว้
            exportData.push(["", "", "", c.company_name]);
          }
        });
      }
    });

    // 3. แปลงเป็นตาราง Excel แบบ Array of Arrays
    const ws = XLSX.utils.aoa_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Guard Assignments");

    // 4. Auto-size columns roughly
    const wscols = [
      { wch: 25 }, // Name
      { wch: 15 }, // PIN
      { wch: 10 }, // Status
      { wch: 50 }, // Assigned Companies
    ];
    ws["!cols"] = wscols;

    // 5. Enable text wrap and top alignment for all cells
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = { c: C, r: R };
        const cell_ref = XLSX.utils.encode_cell(cell_address);
        if (!ws[cell_ref]) continue;
        if (!ws[cell_ref].s) ws[cell_ref].s = {};
        ws[cell_ref].s.alignment = { wrapText: true, vertical: "top" };
      }
    }

    XLSX.writeFile(wb, "Guard_Assignments_Report.xlsx");
  };

  // ============ HELPERS ============

  const filteredGuards = guards.filter((g) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      g.name?.toLowerCase().includes(search) ||
      g.employee_id?.toLowerCase().includes(search) ||
      g.companies?.some((c) => c.company_name?.toLowerCase().includes(search))
    );
  });

  const totalGuards = guards.length;
  const multiCompanyGuards = guards.filter((g) => g.company_count > 1).length;
  const totalAssignments = guards.reduce((sum, g) => sum + g.company_count, 0);

  // Get company IDs already assigned to a guard (for filtering assign modal)
  const getAssignedCompanyIds = (guard) => {
    return (guard?.companies || []).map((c) => c.company_id);
  };

  // ============ TABLE COLUMNS ============

  const columns = [
    {
      title: "ชื่อ",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "รหัส PIN",
      dataIndex: "employee_id",
      key: "employee_id",
      width: 100,
      render: (val) => val || <Text type="secondary">-</Text>,
    },
    {
      title: "บริษัท",
      key: "companies",
      sorter: (a, b) => a.company_count - b.company_count,
      render: (_, record) => {
        if (record.company_count === 0) {
          return <Text type="secondary">ยังไม่ได้มอบหมายบริษัท</Text>;
        }
        return (
          <Space wrap>
            {record.companies.map((c) => (
              <Tag key={c.assignment_id} color="blue">
                {c.company_name}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "สถานะ",
      key: "status",
      width: 100,
      render: (_, record) => (
        <Tag color={record.is_active ? "green" : "red"}>
          {record.is_active ? "ใช้งานอยู่" : "ไม่ใช้งาน"}
        </Tag>
      ),
    },
    {
      title: "จัดการ",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            ดูข้อมูล
          </Button>
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleOpenAssignModal(record)}
          >
            มอบหมาย
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <Title level={3}>
        <TeamOutlined /> จัดการ รปภ.
      </Title>
      <Text type="secondary">เพิ่ม รปภ. และจัดการการมอบหมายบริษัท</Text>

      {/* Summary Cards */}
      <Row gutter={16} style={{ margin: "24px 0" }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="รปภ. ทั้งหมด"
              value={totalGuards}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="รปภ. หลายบริษัท"
              value={multiCompanyGuards}
              prefix={<SwapOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="การมอบหมายทั้งหมด"
              value={totalAssignments}
              prefix={<BankOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Guards Table */}
      <Card
        title="รปภ. ทั้งหมด"
        extra={
          <Space>
            <Input
              placeholder="ค้นหา รปภ..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            {!isMultiCompanyPatrol && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenCreateModal}
              >
                เพิ่ม รปภ.
              </Button>
            )}
            {isSuperAdmin && (
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                ส่งออก Excel
              </Button>
            )}
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredGuards}
          rowKey="id"
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>

      {/* ============ CREATE GUARD MODAL ============ */}
      <Modal
        title="เพิ่ม รปภ. ใหม่"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateGuard}
          requiredMark={false}
        >
          <Form.Item
            name="name"
            label="ชื่อ รปภ."
            rules={[
              { required: true, message: "กรุณาระบุชื่อ รปภ." },
              { min: 2, message: "ชื่อต้องมีอย่างน้อย 2 ตัวอักษร" },
            ]}
          >
            <Input placeholder="ระบุชื่อ รปภ." />
          </Form.Item>

          <Form.Item
            name="password"
            label="รหัส PIN (5 หลัก)"
            rules={[
              { required: true, message: "กรุณาระบุรหัส PIN 5 หลัก" },
              { pattern: /^\d{5}$/, message: "รหัส PIN ต้องมี 5 หลักพอดี" },
            ]}
          >
            <Input
              placeholder="เช่น 80345"
              maxLength={5}
              inputMode="numeric"
              style={{ fontSize: 18, letterSpacing: 8, textAlign: "center" }}
              onChange={() => {
                // Debounce PIN check
                setTimeout(handleCreatePinCheck, 300);
              }}
            />
          </Form.Item>

          <Form.Item
            name="company_ids"
            label="มอบหมายให้บริษัท"
            rules={[
              { required: true, message: "กรุณาเลือกอย่างน้อยหนึ่งบริษัท" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="เลือกบริษัท"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              onChange={() => {
                setTimeout(handleCreatePinCheck, 300);
              }}
            >
              {companies.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {checkingCreatePin && (
            <Alert
              message="กำลังตรวจสอบความซ้ำซ้อนของรหัส PIN..."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {createPinConflicts.length > 0 && (
            <Alert
              message="ตรวจพบรหัส PIN ซ้ำซ้อน"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {createPinConflicts.map((c, i) => (
                    <li key={i}>
                      บริษัท "{c.company_name}": ซ้ำกับ "{c.conflict_with}"
                    </li>
                  ))}
                </ul>
              }
              type="error"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item
            style={{ textAlign: "right", marginTop: 24, marginBottom: 0 }}
          >
            <Space>
              <Button onClick={() => setCreateModalVisible(false)}>
                ยกเลิก
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={creating}
                disabled={createPinConflicts.length > 0 || checkingCreatePin}
              >
                เพิ่ม รปภ.
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ============ ASSIGN MODAL ============ */}
      <Modal
        title={`มอบหมาย "${assignGuard?.name || ""}" ให้บริษัท`}
        open={assignModalVisible}
        onCancel={() => setAssignModalVisible(false)}
        onOk={handleAssign}
        width={800}
        confirmLoading={assigning}
        okText="มอบหมาย"
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Transfer
            dataSource={companies.map((c) => ({
              key: c.id.toString(),
              title: c.name,
            }))}
            titles={["บริษัททั้งหมด", "บริษัทที่มอบหมาย"]}
            targetKeys={assignCompanyIds.map(String)}
            onChange={(targetKeys) =>
              handleAssignCompanyChange(targetKeys.map(Number))
            }
            render={(item) => item.title}
            listStyle={{ width: 350, height: 350 }}
            showSearch
            filterOption={(input, item) =>
              item.title.toLowerCase().includes(input.toLowerCase())
            }
          />
        </Space>
      </Modal>

      {/* ============ DETAIL MODAL ============ */}
      <Modal
        title={`รายละเอียด รปภ.: ${selectedGuard?.name || ""}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedGuard(null);
          setGuardDetail(null);
        }}
        footer={null}
        width={600}
      >
        {guardDetail && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Card
              title={`บริษัทที่รับผิดชอบ (${guardDetail.companies.length})`}
              size="small"
              extra={
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setDetailModalVisible(false);
                    handleOpenAssignModal(selectedGuard);
                  }}
                >
                  เพิ่ม
                </Button>
              }
            >
              {guardDetail.companies.length === 0 ? (
                <Text type="secondary">ยังไม่ได้มอบหมายบริษัท</Text>
              ) : (
                <List
                  dataSource={guardDetail.companies}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <BankOutlined
                            style={{ fontSize: 20, color: "#1890ff" }}
                          />
                        }
                        title={item.company_name}
                        description={
                          <Space>
                            <Text type="secondary">
                              มอบหมายเมื่อ:{" "}
                              {new Date(item.assigned_at).toLocaleDateString()}
                            </Text>
                            {item.assigned_by_name && (
                              <Text type="secondary">
                                โดย {item.assigned_by_name}
                              </Text>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default GuardCompanyManagement;
